import { Box, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import NoDocuments from '../../assets/images/NoDocuments';
import { colors, TextFieldComponent } from '../../components';
import CustomButton from '../../components/button/ButtonComponent';
import DocumentPreviewModal from '../../components/DocumentPreviewModal/DocumentPreviewModal';
import { VerificationBannerContext } from '../../components/layout/dashboard-layout';
import { CustomPagination } from '../../components/pagination/pagination-component';
import { CustomSelect } from '../../components/select/CustomSelect';
import CommonTable from '../../components/tableComponent/commonTableComponent';
import CommonHeader from '../../components/ui-components/CommonHeader';
import { useContracts } from '../../hooks/useContracts';
import { getDocumentPreview, getDocumentsList, uploadDocument } from '../../services/documents-service';
import { getOpenInvoices } from '../../services/payment-services';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import {
    trackDocumentActionFailed,
    trackDocumentDownload,
    trackDocumentPreview,
    trackDocumentUpload,
} from '../../utils/google-analytics';
import {
    ALLOWED_FILE_EXTENSIONS,
    downloadBlobSafely,
    formatToUSDate,
    getMimeType,
    isDocumentPreviewEligible,
    shouldDisablePayNow,
} from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';

import { DocumentMobileCard } from './components/DocumentMobileCard';

import type { ContractApiResponse } from '../../services/contract-services';
import type { OpenInvoiceApiResponse } from '../../services/payment-services';

interface RawDocumentApiResponse {
    Id: string | number;
    DocName: string;
    DocumentCreatedTime: string;
    FileId: string;
    Size?: string | number;
    Type?: string;
}

interface NormalizedDocument extends RawDocumentApiResponse {
    id: string | number;
    name: string;
    date: string;
    fileId: string;
}

type DocumentTableRowData = { originalDocument?: NormalizedDocument } & Record<string, unknown>;

const FILE_TYPE_BG_COLORS: Record<string, string> = {
    pdf: colors.errorPreset[30],
    docx: colors.bluePreset[60],
    doc: colors.bluePreset[60],
    xlsx: colors.successPreset[20],
    xls: colors.successPreset[20],
    csv: colors.successPreset[20],
    txt: colors.greyPreset[20],
    xml: colors.warningPreset[80],
    jpeg: colors.bluePreset[60],
    jpg: colors.bluePreset[60],
};
const FILE_TYPE_COLORS: Record<string, string> = {
    pdf: colors.errorPreset[10],
    docx: colors.bluePreset[30],
    doc: colors.bluePreset[30],
    xlsx: colors.successPreset[50],
    xls: colors.successPreset[50],
    csv: colors.successPreset[50],
    txt: colors.greyPreset[80],
    xml: colors.warningPreset[50],
    jpeg: colors.bluePreset[30],
    jpg: colors.bluePreset[30],
};

const getFileExtension = (filename: string): string => filename.split('.').pop()?.toLowerCase() || '';

const getFileTypeBGColor = (filename: string): string => {
    const ext = getFileExtension(filename);
    return FILE_TYPE_BG_COLORS[ext] || colors.greyPreset[80];
};

const getFileTypeColor = (filename: string): string => {
    const ext = getFileExtension(filename);
    return FILE_TYPE_COLORS[ext] || colors.greyPreset[100];
};

const base64ToBlob = (dataUrlOrBase64: string, mimeType: string): Blob => {
    const base64Data = dataUrlOrBase64.includes('base64,') ? dataUrlOrBase64.split('base64,')[1] : dataUrlOrBase64;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType || 'application/octet-stream' });
};

export const DocumentsOverview: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { contracts: contractsFromStore, fetchContracts } = useContracts();
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const showVerificationBanner = useContext(VerificationBannerContext);
    const [selectedContractId, setSelectedContractId] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const [, setDocCount] = useState<number>(0);
    const [openInvoices, setOpenInvoices] = useState<OpenInvoiceApiResponse[]>([]);
    const [documents, setDocuments] = useState<NormalizedDocument[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openModal, setOpenModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<{ name: string; date: string; fileId: string } | null>(
        null,
    );
    const [previewData, setPreviewData] = useState<string | undefined>(undefined);
    const [fileType, setFileType] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
    const [previewUnavailableIds, setPreviewUnavailableIds] = useState<Set<string>>(() => new Set());
    const fetchingOpenInvoices = useRef(false);
    const contractsFetchAttempted = useRef(false);

    // Ensure contracts are loaded (same as dashboard) so Pay Now disable logic has AccountType, IsLoan, AutoPay
    useEffect(() => {
        const list = contractsFromStore ?? [];
        if (list.length > 0 || contractsFetchAttempted.current) return;
        contractsFetchAttempted.current = true;
        fetchContracts(false).catch(() => undefined);
    }, [contractsFromStore, fetchContracts]);

    const activeContracts = useMemo(() => {
        if (!contractsFromStore || !Array.isArray(contractsFromStore)) return [];
        return contractsFromStore.filter((c: ContractApiResponse) => c?.Status === 1);
    }, [contractsFromStore]);

    const contractOptions = useMemo(
        () =>
            activeContracts.map((c: ContractApiResponse) => ({
                id: String(c.Id),
                name: `${c.AspireId || ''} · ${c.AssetDesc || ''}`.replace(/ · $/, ''),
            })),
        [activeContracts],
    );

    const selectedContract = useMemo(
        () => activeContracts.find((c: ContractApiResponse) => String(c.Id) === selectedContractId),
        [activeContracts, selectedContractId],
    );

    const contractsList = useMemo(() => contractsFromStore ?? [], [contractsFromStore]);
    // Pay Now enable/disable based on the selected contract in the dropdown (same rule as contractDetails)
    const selectedContractPayNowDisabled = useMemo(() => {
        if (!selectedContract) return true;
        const id = selectedContract.AspireId ?? selectedContract.AspireRecordId;
        if (id == null || id === '') return true;
        return shouldDisablePayNow(id, contractsList);
    }, [selectedContract, contractsList]);
    const hasOutstandingBalance =
        selectedContract?.OutstandingBalance && Number(selectedContract.OutstandingBalance) > 0;
    const hasPaymentEligibility = openInvoices.length > 0 || hasOutstandingBalance;
    const isPayNowDisabled = !selectedContract || !hasPaymentEligibility || selectedContractPayNowDisabled;

    const handlePayNow = () => {
        if (!selectedContract) return;
        const contractId = selectedContract.AspireId || `Contract ${selectedContract.Id}`;
        dispatch(showLoader());
        navigate(NavigationConfig.DashboardMakePayment, {
            state: { selectedContractIds: [contractId], fromDocuments: true },
        });
    };

    // Fetch open invoices for Pay All button (same as index.tsx)
    useEffect(() => {
        (async () => {
            try {
                if (fetchingOpenInvoices.current) return;
                fetchingOpenInvoices.current = true;

                const response = await getOpenInvoices(false);
                if (response.statusCode === 200 && Array.isArray(response.data)) {
                    setOpenInvoices(response.data);
                } else {
                    setOpenInvoices([]);
                }
            } catch {
                setOpenInvoices([]);
            }
        })();
    }, []);

    // Select the first contract when options load and none is selected
    useEffect(() => {
        if (contractOptions.length > 0 && !selectedContractId) {
            setSelectedContractId(contractOptions[0].id);
        }
    }, [contractOptions, selectedContractId]);

    const normalizeDocuments = useCallback((rawDocuments: RawDocumentApiResponse[]): NormalizedDocument[] => {
        return (rawDocuments || []).map((doc: RawDocumentApiResponse) => ({
            ...doc,
            id: doc.Id,
            name: doc.DocName,
            date: formatToUSDate(doc.DocumentCreatedTime),
            fileId: doc.FileId,
            Size: doc.Size,
            Type: doc.Type,
        }));
    }, []);

    useEffect(() => {
        if (!selectedContractId) {
            setDocCount(0);
            setDocuments([]);
            return;
        }
        let cancelled = false;
        getDocumentsList(selectedContractId, true)
            .then(res => {
                if (!cancelled && res?.statusCode === 200 && Array.isArray(res.data)) {
                    const list = normalizeDocuments(res.data || []);
                    setDocuments(list);
                    setDocCount(list.length);
                } else if (!cancelled) {
                    setDocuments([]);
                    setDocCount(0);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDocuments([]);
                    setDocCount(0);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [selectedContractId, normalizeDocuments]);

    useEffect(() => {
        setPreviewUnavailableIds(new Set());
    }, [selectedContractId]);

    const markPreviewUnavailable = useCallback((fileId: string) => {
        setPreviewUnavailableIds(prev => {
            const next = new Set(prev);
            next.add(String(fileId));
            return next;
        });
    }, []);

    const convertBytesToKB = (bytes: string | number): string => {
        const bytesNum = typeof bytes === 'string' ? parseFloat(bytes) : bytes;
        if (isNaN(bytesNum) || bytesNum === 0) return '0 KB';
        const kb = bytesNum / 1024;
        return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(2)} KB`;
    };

    // Generate dynamic category filter options from documents
    const dynamicCategoryFilterOptions = useMemo(() => {
        const uniqueTypes = new Set<string>();
        documents.forEach((doc: NormalizedDocument) => {
            if (doc.Type) {
                uniqueTypes.add(doc.Type);
            }
        });
        return Array.from(uniqueTypes).map(type => ({
            value: type,
            label: type,
        }));
    }, [documents]);

    const filteredDocuments = useMemo(() => {
        let list = documents;
        if (categoryFilter) {
            list = list.filter((doc: NormalizedDocument) => doc.Type === categoryFilter);
        }
        if (!searchTerm.trim()) return list;
        const term = searchTerm.trim().toLowerCase();
        return list.filter(
            (doc: NormalizedDocument) =>
                (doc.name && doc.name.toLowerCase().includes(term)) ||
                (doc.Type && String(doc.Type).toLowerCase().includes(term)),
        );
    }, [documents, searchTerm, categoryFilter]);

    const documentTableRows = useMemo(() => {
        return filteredDocuments.map((doc: NormalizedDocument) => {
            const ext = getFileExtension(doc.name || '').toUpperCase();
            const title = doc.name?.replace(/\.[^.]+$/, '') || doc.name || '';
            const docNameCell = (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: getFileTypeBGColor(doc.name),
                            color: getFileTypeColor(doc.name),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            fontWeight: 600,
                        }}
                    >
                        {ext || 'FILE'}
                    </Box>
                    <Stack gap={0.25}>
                        <Tooltip title={doc.name || ''} arrow placement="bottom">
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                color="textPrimary"
                                noWrap
                                sx={{ maxWidth: 220 }}
                            >
                                {title}
                            </Typography>
                        </Tooltip>
                        <Tooltip title={doc.name || ''} arrow placement="bottom">
                            <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 220 }}>
                                {doc.name}
                            </Typography>
                        </Tooltip>
                    </Stack>
                </Box>
            );
            return {
                documentName: docNameCell,
                date: doc.date,
                fileType: doc.Type || '—',
                size: convertBytesToKB(doc.Size || 0),
                originalDocument: doc,
            };
        });
    }, [filteredDocuments]);

    const paginatedRows = useMemo(() => {
        const start = currentPage * rowsPerPage;
        const end = rowsPerPage === -1 ? documentTableRows.length : start + rowsPerPage;
        return documentTableRows.slice(start, end);
    }, [documentTableRows, currentPage, rowsPerPage]);

    const documentTableColumns = useMemo(
        () => [
            { id: 'documentName', label: t('Documents.table.documentName') || 'Document Name' },
            { id: 'date', label: t('Documents.table.date') || 'Created', sortable: true },
            {
                id: 'fileType',
                label: t('Documents.table.fileType') || 'Category',
                filterOptions: dynamicCategoryFilterOptions,
            },
            { id: 'size', label: t('Documents.table.size') || 'File Size' },
        ],
        [t, dynamicCategoryFilterOptions],
    );

    const isDocumentActionsBlockedForImpersonation = impersonation?.isImpersonating;

    const fetchDocumentPreview = async (documentId: string, filename: string) => {
        if (isDocumentActionsBlockedForImpersonation) {
            return;
        }
        try {
            const response = await getDocumentPreview(documentId, true);
            setPreviewData(undefined);
            if (response.statusCode === 200) {
                const base64Data = response.data;
                const invalidPayload = base64Data == null || typeof base64Data !== 'string' || base64Data.trim() === '';
                if (invalidPayload) {
                    markPreviewUnavailable(documentId);
                    return;
                }
                const mimeType = getMimeType(filename);
                setFileType(mimeType);
                const dataUrl = base64Data.startsWith('data:') ? base64Data : `data:${mimeType};base64,${base64Data}`;
                setPreviewData(dataUrl);
                setOpenModal(true);
                trackDocumentPreview(filename, selectedContractId);
            } else {
                trackDocumentActionFailed('preview', response.message, selectedContractId);
                markPreviewUnavailable(documentId);
            }
        } catch {
            trackDocumentActionFailed('preview', 'exception', selectedContractId);
            markPreviewUnavailable(documentId);
        } finally {
            dispatch(hideLoader());
        }
    };

    const canShowDocumentPreview = (row: DocumentTableRowData): boolean => {
        if (impersonation?.isImpersonating) return false;
        const doc = row.originalDocument;
        if (!doc?.fileId || !doc?.name) return false;
        if (!isDocumentPreviewEligible(doc.name)) return false;
        return !previewUnavailableIds.has(String(doc.fileId));
    };

    const handleViewAction = (row: DocumentTableRowData) => {
        if (impersonation?.isImpersonating) return;
        if (!canShowDocumentPreview(row) || !row.originalDocument) return;
        setSelectedDocument(row.originalDocument);
        fetchDocumentPreview(row.originalDocument.fileId, row.originalDocument.name);
    };

    const showToast = useCallback(
        (message: string, severity: 'success' | 'error' | 'info') => {
            dispatch(openToast({ show: true, isAlertOpen: true, message, severity }));
        },
        [dispatch],
    );

    const handleDownloadAction = async (row: DocumentTableRowData) => {
        if (impersonation?.isImpersonating) return;
        const doc = row.originalDocument;
        if (!doc) return;
        try {
            const response = await getDocumentPreview(doc.fileId, true);
            if (response.statusCode === 200 && response.data) {
                const mimeType = getMimeType(doc.name);
                const dataUrl = `data:${mimeType};base64,${response.data}`;
                const blob = base64ToBlob(dataUrl, mimeType);
                downloadBlobSafely(blob, doc.name);
                trackDocumentDownload(doc.name, selectedContractId);
                showToast('File Downloaded Successfully.', 'success');
            }
        } catch (error) {
            console.error('Error downloading file:', error);
            trackDocumentActionFailed('download', 'exception', selectedContractId);
            showToast('Failed To Download File', 'error');
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedDocument(null);
    };

    const handleUploadClick = () => {
        if (impersonation?.isImpersonating) return;
        fileInputRef.current?.click();
    };

    const validateFileExtension = (fileName: string): boolean => {
        const extension = getFileExtension(fileName);
        return extension ? ALLOWED_FILE_EXTENSIONS.includes(extension) : false;
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (impersonation?.isImpersonating) {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            showToast('Upload is disabled while impersonating.', 'info');
            return;
        }

        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        // Validate file extension
        if (!validateFileExtension(file.name)) {
            const fileExtension = getFileExtension(file.name) || 'unknown';
            showToast(`Unsupported File Type. ${fileExtension} Files Are Not Allowed.`, 'error');
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        // Validate file size - must be greater than 0 KB
        if (file.size === 0) {
            showToast('The File Is Empty. Please Upload A Valid File.', 'error');
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        if (!selectedContract?.Id) {
            showToast('Please Select A Contract First', 'error');
            return;
        }

        try {
            dispatch(showLoader());
            const response = await uploadDocument(selectedContract.Id.toString(), file, true);
            if (response.statusCode === 200) {
                trackDocumentUpload(file.name, selectedContractId, getFileExtension(file.name) || undefined);
                showToast('Document Uploaded Successfully', 'success');
                // Refresh documents list
                if (selectedContractId) {
                    getDocumentsList(selectedContractId, true)
                        .then(res => {
                            if (res?.statusCode === 200 && Array.isArray(res.data)) {
                                const list = normalizeDocuments(res.data || []);
                                setDocuments(list);
                                setDocCount(list.length);
                            }
                        })
                        .catch(() => {
                            setDocuments([]);
                            setDocCount(0);
                        });
                }
            } else {
                trackDocumentActionFailed('upload', response.message, selectedContractId);
                showToast(response.message || 'Failed To Upload Document', 'error');
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            const uploadErr = error as { message?: string; response?: { data?: { message?: string } } };
            const errorMessage =
                uploadErr?.response?.data?.message || uploadErr?.message || 'Failed To Upload Document';
            trackDocumentActionFailed('upload', errorMessage, selectedContractId);
            showToast(errorMessage, 'error');
        } finally {
            dispatch(hideLoader());
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handlePageChange = (newPage: number, newRowsPerPage: number) => {
        setCurrentPage(newPage);
        setRowsPerPage(newRowsPerPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setCurrentPage(0);
        setRowsPerPage(newRowsPerPage);
    };

    useEffect(() => {
        if (selectedContract) setCurrentPage(0);
    }, [searchTerm, selectedContract, categoryFilter]);

    return (
        <Stack sx={{ p: 3, pb: 0, height: '100%' }}>
            <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
            <Stack
                direction={'row'}
                gap={2}
                width={'100%'}
                flexWrap={'wrap'}
                alignItems={{ xs: 'center', lg: 'center' }}
            >
                <Box sx={{ flexGrow: 1, minWidth: 'fit-content', order: 1 }}>
                    <CommonHeader
                        mainTitle={t('Documents.header.mainTitle')}
                        subTitle={t('Documents.header.subTitle')}
                        icon="icon-documents"
                    />
                </Box>
                <Box sx={{ flexShrink: 0, order: { xs: 2, lg: 5 } }}>
                    <CustomButton
                        variant="contained"
                        sx={{
                            width: 'fit-content',
                            minWidth: 110,
                            fontWeight: 600,
                            transition: 'all 0.2s ease',
                            pointerEvents: isPayNowDisabled ? 'none' : 'auto',
                            cursor: isPayNowDisabled ? 'not-allowed' : 'pointer',
                            '&:hover:not(:disabled)': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            },
                        }}
                        onClick={handlePayNow}
                        disabled={isPayNowDisabled}
                    >
                        {t('Documents.header.payNow')}
                    </CustomButton>
                </Box>
                <Box width={{ xs: '100%', sm: 220, lg: 220 }} sx={{ order: { xs: 3, lg: 2 } }}>
                    <CustomSelect
                        value={selectedContractId}
                        onChange={value => {
                            setSelectedContractId(value);
                            setCategoryFilter(null);
                        }}
                        options={contractOptions}
                        placeholder={t('Documents.contractId') || 'Select contract'}
                        noOptionsMessage={t('Documents.noDocumentsUploaded') ? 'No contracts' : undefined}
                        renderContractItemStyle
                    />
                </Box>
                {selectedContract && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={2}
                        width={{ xs: '100%', lg: 'auto' }}
                        sx={{ order: { xs: 4, lg: 3 } }}
                    >
                        <Box sx={{ flexGrow: { xs: 1, lg: 0 }, width: { xs: 'auto', sm: 220, lg: 220 } }}>
                            <TextFieldComponent
                                placeholder={t('Documents.header.search')}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                startIcon={
                                    <span
                                        className="icon-search"
                                        style={{ color: theme.palette.text.secondary, fontSize: 16 }}
                                    />
                                }
                                className="searchTextFiled"
                            />
                        </Box>
                        {selectedContract?.IsPrimary && (
                            <Box sx={{ flexShrink: 0 }}>
                                <CustomButton
                                    variant="outlined"
                                    startIcon={<span className="icon-upload" />}
                                    onClick={handleUploadClick}
                                    sx={{
                                        width: 'fit-content',
                                        pointerEvents: isDocumentActionsBlockedForImpersonation ? 'none' : 'auto',
                                        cursor: isDocumentActionsBlockedForImpersonation ? 'not-allowed' : 'pointer',
                                    }}
                                    disabled={isDocumentActionsBlockedForImpersonation}
                                >
                                    {t('Documents.header.upload')}
                                </CustomButton>
                            </Box>
                        )}
                    </Stack>
                )}
            </Stack>
            <Stack
                gap={2}
                sx={{
                    height: '100%',
                    mt: 2,
                    justifyContent: filteredDocuments.length > 0 ? 'space-between' : 'center',
                    position: 'relative',
                    width: '100%',
                }}
            >
                {selectedContract && (
                    <>
                        {filteredDocuments.length > 0 ? (
                            <Stack gap={3} sx={{ height: '100%', justifyContent: 'space-between' }}>
                                {isMobileOrTablet ? (
                                    <Stack gap={1.5} sx={{ overflowY: 'auto' }}>
                                        {paginatedRows.map((row, rowIndex) => (
                                            <DocumentMobileCard
                                                key={String(row.originalDocument?.fileId ?? rowIndex)}
                                                row={row}
                                                canPreview={
                                                    !isDocumentActionsBlockedForImpersonation &&
                                                    canShowDocumentPreview(row)
                                                }
                                                canDownload={!isDocumentActionsBlockedForImpersonation}
                                                onView={() => handleViewAction(row)}
                                                onDownload={() => handleDownloadAction(row)}
                                            />
                                        ))}
                                    </Stack>
                                ) : (
                                    <CommonTable
                                        columns={documentTableColumns}
                                        rows={paginatedRows}
                                        onRowClick={row => {
                                            if (isDocumentActionsBlockedForImpersonation) return;
                                            if (!canShowDocumentPreview(row) || !row.originalDocument) return;
                                            const originalDoc = row.originalDocument as
                                                | { name: string; date: string; fileId: string }
                                                | undefined;
                                            if (!originalDoc) return;
                                            setSelectedDocument(originalDoc);
                                            fetchDocumentPreview(originalDoc.fileId, originalDoc.name);
                                        }}
                                        isActions={!isDocumentActionsBlockedForImpersonation}
                                        actions={[
                                            {
                                                icon: 'icon-eye',
                                                onClick: handleViewAction,
                                                tooltip: t('common.Preview') || 'View',
                                                visible: row =>
                                                    !isDocumentActionsBlockedForImpersonation &&
                                                    canShowDocumentPreview(row),
                                            },
                                            {
                                                icon: 'icon-download',
                                                onClick: handleDownloadAction,
                                                tooltip: t('common.Download') || 'Download',
                                                visible: () => !isDocumentActionsBlockedForImpersonation,
                                            },
                                        ]}
                                        actionsLabel={
                                            !isDocumentActionsBlockedForImpersonation
                                                ? t('Documents.table.actions') || 'Actions'
                                                : undefined
                                        }
                                        serverSide={false}
                                        stickyHeader
                                        maxHeight={
                                            showVerificationBanner ? 'calc(100dvh - 307px)' : 'calc(100dvh - 270px)'
                                        }
                                        columnFilterValues={{ fileType: categoryFilter }}
                                        onFilterChange={(columnId, value) => {
                                            if (columnId === 'fileType') setCategoryFilter(value);
                                        }}
                                        sx={{
                                            '& .tableHeaderCell': { minWidth: 100 },
                                            '& .MuiTableBody-root': {
                                                '& .MuiTableCell-root': {
                                                    p: '16px !important',
                                                },
                                            },
                                            '& .MuiTableHead-root .MuiTableCell-root:first-of-type': {
                                                minWidth: 320,
                                            },
                                        }}
                                    />
                                )}
                                <CustomPagination
                                    totalCount={documentTableRows.length}
                                    rowsPerPage={rowsPerPage}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                    serverSide={false}
                                    sortedRowsLength={documentTableRows.length}
                                />
                            </Stack>
                        ) : (
                            <Stack
                                width={'100%'}
                                justifyContent="center"
                                alignItems="center"
                                sx={{
                                    position: 'absolute',
                                    top: '35%',
                                    left: '50%',
                                    transform: 'translate(-50%, -30%)',
                                }}
                            >
                                <Stack
                                    justifyContent="center"
                                    alignItems="center"
                                    gap={1.5}
                                    sx={{
                                        '& svg': {
                                            width: '40%',
                                        },
                                    }}
                                >
                                    <NoDocuments />
                                    <Typography variant="h6" color="textSecondary" textAlign={'center'}>
                                        {t('Documents.noDocumentsUploaded')}
                                    </Typography>
                                </Stack>
                            </Stack>
                        )}
                    </>
                )}
            </Stack>
            <DocumentPreviewModal
                isOpen={openModal}
                onClose={handleCloseModal}
                documentName={selectedDocument?.name}
                documentDate={selectedDocument?.date}
                previewData={previewData}
                fileType={fileType}
                showDownloadButton={!impersonation?.isImpersonating}
                primaryBtnName={t('common.Cancel')}
                secondaryBtnName={t('common.Download')}
                onDownload={
                    !impersonation?.isImpersonating && previewData && selectedDocument
                        ? () => {
                              const blob = base64ToBlob(previewData, fileType);
                              downloadBlobSafely(blob, selectedDocument.name);
                              showToast('File Downloaded Successfully.', 'success');
                          }
                        : undefined
                }
            />
        </Stack>
    );
};
